import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PrivacyAcceptanceModal from "./PrivacyAcceptanceModal";
import PasswordChangeModal from "./PasswordChangeModal";

// Icons (simple SVG)
const MenuIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const UserIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

// Navigation items con sezione di appartenenza per visibilità dinamica
const allNavItems = [
  { path: "/", label: "Dashboard", icon: "📊", section: "dashboard" },
  { path: "/rsa", label: "RSA", icon: "🏥", section: "rsa" },
  {
    path: "/apparecchiature",
    label: "Apparecchiature",
    icon: "⚙️",
    section: "apparecchiature",
  },
  {
    path: "/impianti",
    label: "Impianti",
    icon: "☀️",
    section: "impianti",
  },
  { path: "/mappe", label: "Mappe", icon: "🗺️", section: "mappe" },
  { path: "/fornitori", label: "Fornitori", icon: "🏢", section: "fornitori" },
  { path: "/contratti", label: "Contratti", icon: "📋", section: "contratti" },
  { path: "/scadenze", label: "Scadenze", icon: "📅", section: "scadenze" },
  {
    path: "/calendario",
    label: "Calendario",
    icon: "🗓️",
    section: "calendario",
  },
  {
    path: "/interventi",
    label: "Interventi",
    icon: "🔧",
    section: "interventi",
  },
  {
    path: "/richieste-intervento",
    label: "Richieste Intervento",
    icon: "📝",
    section: "richieste_intervento",
  },
  {
    path: "/consumi-utenze",
    label: "Bollette e Consumi",
    icon: "💡",
    section: "consumi_utenze",
  },
  {
    path: "/azioni-correttive",
    label: "Azioni Correttive",
    icon: "⚠️",
    section: "azioni_correttive",
  },
  {
    path: "/valutazione-fornitori",
    label: "Valutazione Fornitori",
    icon: "⭐",
    section: "valutazione_fornitori",
  },
  {
    path: "/rapportini",
    label: "Rapportini",
    icon: "📄",
    section: "rapportini",
  },
  {
    path: "/documenti-immobili",
    label: "Documenti Immobili",
    icon: "🏠",
    section: "documenti_immobili",
  },
  {
    path: "/email-processing",
    label: "Log Email Bollette",
    icon: "📧",
    section: "email_processing",
  },
  {
    path: "/bollette-review",
    label: "Bollette da Rivedere",
    icon: "🔍",
    section: "bollette_review",
  },
  {
    path: "/segnalazioni-obbligatori",
    label: "Obblighi Normativi",
    icon: "⚖️",
    section: "segnalazioni_obbligatori",
  },
];

// Admin nav item
const adminNavItem = {
  path: "/admin",
  label: "Amministrazione",
  icon: "⚙️",
  section: "admin",
};

function Layout() {
  const { user, logout, canAccessSection } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Filter nav items by dynamic section permissions
  const navItems = allNavItems.filter((item) => canAccessSection(item.section));
  const hasAdminAccess = canAccessSection("admin");
  const showPrivacyModal = user && !user.privacyAccettata && !user.isTestUser;
  const showPasswordChangeModal =
    user && !user.isTestUser && user.privacyAccettata && user.passwordScaduta;

  const handleLogout = () => {
    console.log("=== HANDLE LOGOUT CLICKED ===");
    console.log("[LAYOUT] handleLogout called, invoking logout()...");
    logout();
    console.log("[LAYOUT] logout() returned");
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 w-64 bg-white shadow-lg transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 transition-transform duration-300 ease-in-out z-30 flex flex-col`}
      >
        {/* Logo */}
        <div className="h-20 flex items-center justify-between px-4 border-b">
          <div className="flex items-center gap-3">
            <img
              src="/logo-phc.jpg"
              alt="Peralba Health Care"
              className="h-12 w-auto"
            />
            <p className="text-sm text-primary-600 font-medium">
              Portale Manutenzioni
            </p>
          </div>
          <button
            className="lg:hidden p-2"
            onClick={() => setSidebarOpen(false)}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-4 px-2 flex-1 overflow-y-auto pb-4">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                  isActive
                    ? "bg-primary-100 text-primary-700"
                    : "text-gray-600 hover:bg-gray-100"
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}

          {/* Admin section - only visible for Admin/RSPP */}
          {hasAdminAccess && (
            <>
              <div className="border-t my-3"></div>
              <NavLink
                to={adminNavItem.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                    isActive
                      ? "bg-amber-100 text-amber-700"
                      : "text-gray-600 hover:bg-gray-100"
                  }`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <span>{adminNavItem.icon}</span>
                <span className="font-medium">{adminNavItem.label}</span>
              </NavLink>
            </>
          )}

          {/* Guida Portale - solo per fornitori */}
          {user?.ruolo === 100000005 && (
            <>
              <div className="border-t my-3"></div>
              <a
                href="/guida-fornitori.html"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors text-gray-600 hover:bg-gray-100"
                onClick={() => setSidebarOpen(false)}
              >
                <span>📖</span>
                <span>Guida Portale</span>
              </a>
            </>
          )}
        </nav>

        {/* User info at bottom */}
        <div className="shrink-0 p-4 border-t bg-gray-50">
          <Link
            to="/profilo"
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors mb-2"
            onClick={() => setSidebarOpen(false)}
          >
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold text-primary-600">
                {user?.displayName?.charAt(0)?.toUpperCase() || "?"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900 truncate">
                {user?.displayName || "Utente"}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {user?.ruoloLabel || user?.email}
              </div>
            </div>
          </Link>
          <a
            href="/api/privacy/informativa"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-xs text-gray-400 hover:text-gray-600 px-2 py-1"
          >
            Informativa Privacy
          </a>
          <button
            onClick={handleLogout}
            className="w-full text-left text-sm text-red-600 hover:text-red-700 px-2 py-1"
          >
            Esci
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top header */}
        <header className="h-16 bg-white shadow-sm flex items-center px-4 lg:px-6">
          <button
            className="lg:hidden p-2 mr-2"
            onClick={() => setSidebarOpen(true)}
          >
            <MenuIcon />
          </button>
          <div className="flex-1">
            {/* Breadcrumb or page title could go here */}
          </div>

          {/* Desktop user menu */}
          <div className="hidden lg:flex items-center gap-4">
            <span className="text-sm text-gray-500">
              {new Date().toLocaleDateString("it-IT", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>

            {/* User dropdown */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-primary-600">
                    {user?.displayName?.charAt(0)?.toUpperCase() || "?"}
                  </span>
                </div>
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-20">
                    <div className="px-4 py-3 border-b">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.displayName}
                      </p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/profilo"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <UserIcon />
                        Il mio profilo
                      </Link>
                      <a
                        href="/api/privacy/informativa"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                        Informativa Privacy
                      </a>
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        Esci
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Mobile: show date only */}
          <div className="lg:hidden text-sm text-gray-500">
            {new Date().toLocaleDateString("it-IT")}
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* Privacy acceptance modal */}
      {showPrivacyModal && <PrivacyAcceptanceModal />}

      {/* Password change modal (shown after privacy is accepted) */}
      {showPasswordChangeModal && <PasswordChangeModal isExpired />}
    </div>
  );
}

export default Layout;
